import React from 'react';
import clsx from 'clsx';

import Pic from './Pic';
import Info from './Info';

import styles from './Result.module.scss';
import Label from './Label';
import Icon from './Icon';
import Button, { ButtonStyles } from './Button';
import { useTCLog } from '../utils/TCEvent';
import {
	AGENCY_PROJECT,
	CURRENT_CRI_LIST,
	FILIALI_PROJECT,
	PROJECT_CAR,
	PROJECT_CAR2,
	PROJECT_CGR,
	PROJECT_CSV,
	PROJECT_SOS,
	TC_EVENT_NAMES
} from '../utils/Constants';
import { useDeviceDetection } from '../utils/DeviceDetection';
import { hasBrandLabel } from '../api/brandUtil';

export default function Result({
	result,
	type,
	onResultClick,
	highlighted,
	onCalcRouteClick,
	onStreetViewClick,
	onDetailClick
}) {
	const isMobile = useDeviceDetection();
	const [TCLog] = useTCLog();
	const [focus, setFocus] = React.useState(false);
	const [showTimeTable, setShowTimeTable] = React.useState(false);

	const isBrandLabel = hasBrandLabel(result);

	const project = result.project;

	if (type === undefined) {
		type = ResultTypes.SIDE_BAR;
	}

	const sideEventName = ResultTypes.SIDE_BAR
		? project === AGENCY_PROJECT
			? result.subAgencyId
				? TC_EVENT_NAMES.SEL_SIDEBAR_SUBAGENCY
				: TC_EVENT_NAMES.SEL_SIDEBAR_AGENCY
			: project === PROJECT_CAR
			? TC_EVENT_NAMES.SEL_SIDEBAR_CAR
			: CURRENT_CRI_LIST.includes(project)
			? TC_EVENT_NAMES.CONTACT_PARTNER + project
			: project === PROJECT_CSV
			? TC_EVENT_NAMES.SEL_SIDEBAR_CSV
			: null
		: null;

	const calcRouteEventName = TC_EVENT_NAMES.CALC_ROUTE;

	const streetViewEventName = TC_EVENT_NAMES.STREET_VIEW;

	const carrozzerieProject =
		project === PROJECT_CAR || project === PROJECT_CAR2;

	return type === ResultTypes.SIDE_BAR ? (
		<li
			id={`result-${result.id}`}
			className={clsx(styles.root, highlighted ? styles.clicked : null)}
			onMouseOver={() => setFocus(true)}
			onMouseOut={() => setFocus(false)}
			onClick={() => {
				TCLog(sideEventName);
				onResultClick(result);
			}}
		>
			<div
				className={clsx(
					styles.infoContainer,
					isBrandLabel ? styles.infoContainerColumn : null
				)}
			>
				<Pic
					focus={focus || highlighted}
					brand={result.brand}
					distance={result.distance}
				/>
				<Info
					focus={focus || highlighted}
					name={result.name}
					linkText={result.cta}
					isRankedAgency={result.isRankedAgency}
					variant={isBrandLabel ? 'block' : null}
					icons={result.ResultIcons}
				/>
			</div>
		</li>
	) : (
		<div className={styles.row}>
			<div className={styles.box}>
				<div
					className={clsx(
						styles.infoContainer,
						isBrandLabel ? styles.infoContainerColumn : null
					)}
				>
					<Pic brand={result.brand} distance={result.distance} />
					<Info
						name={result.name}
						address={result.address}
						flex={false}
						calcRoute={true}
						streetView={true}
						variant={isBrandLabel ? 'block' : null}
						isRankedAgency={result.isRankedAgency}
						onCalcRouteClick={() => {
							TCLog(calcRouteEventName);
							onCalcRouteClick(result);
						}}
						onStreetViewClick={() => {
							TCLog(streetViewEventName);
							onStreetViewClick(result);
						}}
					/>
				</div>
			</div>
			<div className={clsx(styles.box, styles.column)}>
				{result.phone ? (
					<Label
						text={result.phone}
						icon={
							<Icon
								icon="icon/telefono_blue.png"
								size={isMobile ? 'large' : 'small'}
								textOn="right"
							/>
						}
						link={isMobile ? `tel:${result.phone}` : null}
						variant="small"
						hideText={isMobile}
					/>
				) : null}
				{result.fax ? (
					<Label
						text={result.fax}
						icon={
							<Icon
								icon="icon/fax_blue.png"
								size={isMobile ? 'large' : 'small'}
								textOn="right"
							/>
						}
						link={isMobile ? `tel:${result.fax}` : null}
						variant="small"
						hideText={isMobile}
					/>
				) : null}
				{result.email ? (
					<Label
						text={result.email}
						icon={
							<Icon
								icon="icon/mail_blue.png"
								size={isMobile ? 'large' : 'small'}
								textOn="right"
							/>
						}
						link={`mailto:${result.email}`}
						variant="small"
						noUppercase={true}
						hideText={isMobile}
						onClick={() =>
							TCLog(
								project === AGENCY_PROJECT
									? TC_EVENT_NAMES.MAIL_AGENCY
									: project === PROJECT_CAR
									? TC_EVENT_NAMES.MAIL_CAR
									: CURRENT_CRI_LIST.includes(project)
									? TC_EVENT_NAMES.MAIL_CRI
									: null
							)
						}
					/>
				) : null}
				{result.site ? (
					<Label
						text="Visita il sito"
						icon={
							<Icon
								icon="icon/sito_blue.png"
								size={isMobile ? 'large' : 'small'}
								textOn="right"
							/>
						}
						link={result.site}
						variant="small"
						noUppercase={true}
						hideText={isMobile}
					/>
				) : null}
				{result.facebook ? (
					<Label
						text="Visita la pagina facebook"
						icon={
							<Icon
								icon="icon/facebook_blue.png"
								size={isMobile ? 'large' : 'small'}
								textOn="right"
							/>
						}
						link={result.facebook}
						variant="small"
						noUppercase={true}
						hideText={isMobile}
					/>
				) : null}
				{isMobile ? (
					(project === AGENCY_PROJECT || project === PROJECT_CAR) &&
					result.timeTable ? (
						<Button
							icon={
								<Icon
									icon="icon/orari_blue.png"
									size={'large'}
								/>
							}
							variant={ButtonStyles.GHOST}
							onClick={() => setShowTimeTable(!showTimeTable)}
						/>
					) : null
				) : (
					<div>{result.additionalInfo}</div>
				)}
			</div>
			{isMobile ? <div>{result.additionalInfo}</div> : null}
			<div
				className={clsx(
					styles.box,
					isMobile && project === AGENCY_PROJECT && !showTimeTable
						? styles.hide
						: null
				)}
			>
				{result.thirdColumnList ? result.thirdColumnList : null}
			</div>
			<div
				className={clsx(
					styles.box,
					isMobile && project === PROJECT_CAR && !showTimeTable
						? styles.hide
						: null
				)}
			>
				<div
					onClick={
						result.detail
							? () => onDetailClick(result)
							: project === PROJECT_CSV ||
							  project === PROJECT_SOS ||
							  project === PROJECT_CGR ||
							  project == FILIALI_PROJECT
							? () => {
									TCLog(calcRouteEventName);
									onCalcRouteClick(result);
							  }
							: null
					}
				>
					{result.fourthColumnList ? result.fourthColumnList : null}
				</div>
			</div>
		</div>
	);
}

export const ResultTypes = {
	SIDE_BAR: 0,
	RESULTS_LIST: 1
};
